import { useState } from 'react'
import { NavLink } from 'react-router'
import { Icon, SideBar } from 'shared-ui/components'
import { SpriteMap } from 'shared-ui/global'
import { createVariants } from 'shared-ui/lib'
import { isNonNil } from 'utils'

export type NAVSection = {
  pathname: string
  label: string
  icon: React.ReactNode
}

type NAVSectionWithChildren = {
  label: string
  icon: React.ReactNode
  children: NAVSection[]
  pathname?: string
}

//NavLink and NavSection button shared classes
const ButtonClassName = {
  base: 'flex w-full items-center rounded-lg text-left ',
  inActive: 'hover:text-gray-900 '
}

const navLinkClassName = ({ isActive }: { isActive: boolean }) =>
  ButtonClassName.base +
  'gap-3 border-2 border-transparent px-2.5 py-2 leading-7 ' +
  (isActive
    ? 'pan-body-bold bg-accent-100 [&_svg]:!text-green-700 text-green-700'
    : `pan-body text-gray-900 ${ButtonClassName.inActive}`)

const childNavLinkClassName = ({ isActive }: { isActive: boolean }) =>
  ButtonClassName.base +
  'gap-2 h-10 px-4 last:mb-2.5 ' +
  (isActive
    ? 'font-bold bg-accent-100 [&_svg]:!text-green-700 text-green-700'
    : `${ButtonClassName.inActive}text-gray-900 [&_svg]:!text-gray-900`)

export const NavButton = ({ pathname, label, icon }: NAVSection) => (
  <NavLink to={pathname} end className={navLinkClassName}>
    {icon} {label}
  </NavLink>
)

export const NavSection = ({
  label,
  icon,
  sections,
  pathname
}: NAVSection & { sections: NAVSection[] }) => {
  const within = sections.some((child) => child.pathname === pathname)
  const [open, setOpen] = useState(within)

  return (
    <div
      className={`overflow-hidden rounded-lg border-2 ${within ? 'border-green-500 text-green-700' : 'border-transparent'}`}
    >
      <button onClick={() => setOpen((open) => !open)} className={sectionButtonClasses({ within })}>
        <div className={`flex items-center gap-3 ${within ? '[&_svg]:!text-green-700' : ''}`}>
          {icon} {label}
        </div>
        <div className={downIconClasses({ open })}>
          <Icon
            icon={SpriteMap.ChevronIcon}
            className={`size-3 ${within ? '' : 'text-gray-400'}`}
          />
        </div>
      </button>
      <ul className={subSectionClasses({ show: open })}>
        {sections.map((child) => (
          <NavLink
            onClick={() => setOpen(true)}
            key={child.pathname}
            to={child.pathname}
            end
            className={childNavLinkClassName}
          >
            {child.icon} {child.label}
          </NavLink>
        ))}
      </ul>
    </div>
  )
}

const sectionButtonClasses = createVariants({
  base: 'z-10 flex w-full items-center justify-between rounded-lg px-2.5 py-2 text-left leading-7',
  variants: { within: { true: 'pan-body-bold', false: 'pan-body' } }
})

const downIconClasses = createVariants({
  base: 'transition-transform',
  variants: { open: { true: '', false: 'rotate-180' } }
})

const subSectionClasses = createVariants({
  base: 'pan-body-small w-full px-2.5 duration-300 z-0 transition-discrete',
  variants: {
    show: {
      true: 'max-h-[500px]', //max height needed for open and close animation
      false: 'max-h-0 overflow-hidden'
    }
  }
})

export const NavigationSidebar = ({
  pathname,
  sections
}: {
  pathname: string
  sections: (NAVSection | NAVSectionWithChildren)[]
}) => {
  return (
    <SideBar
      items={sections.map((s) => ({
        key: s.label,
        component:
          'children' in s && s.children.length > 0 ? (
            <NavSection pathname={pathname} label={s.label} icon={s.icon} sections={s.children} />
          ) : (
            <NavButton
              pathname={isNonNil(s.pathname) ? s.pathname : ''}
              label={s.label}
              icon={s.icon}
            />
          )
      }))}
    />
  )
}
